import { useState, type FormEvent } from 'react'
import {
  Eye,
  EyeOff,
  KeyRound,
  LoaderCircle,
  Moon,
  Sprout,
  Sun,
} from 'lucide-react'
import type { Theme } from '../types'

type LoginScreenProps = {
  theme: Theme
  busy: boolean
  error: string
  onThemeChange: (theme: Theme) => void
  onLogin: (username: string, password: string) => Promise<boolean>
}

export function LoginScreen({
  theme,
  busy,
  error,
  onThemeChange,
  onLogin,
}: LoginScreenProps) {
  const [username, setUsername] = useState('silacel')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!username.trim() || !password || busy) return
    if (await onLogin(username, password)) setPassword('')
  }

  return (
    <main className="login-screen">
      <button
        type="button"
        className="login-theme-toggle icon-button"
        onClick={() => onThemeChange(theme === 'dark' ? 'light' : 'dark')}
        title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
      >
        {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
      </button>

      <section className="login-card">
        <div className="login-card__art" aria-hidden="true">
          <span className="login-card__brand">
            <Sprout size={22} />
          </span>
          <div className="login-vine login-vine--one" />
          <div className="login-vine login-vine--two" />
          <div className="login-panel-word">
            PRIVATE
            <br />
            GARDEN
          </div>
          <small>
            A STORY CAN BE QUIET
            <br />
            BEFORE IT BRANCHES.
          </small>
        </div>

        <form className="login-form" onSubmit={submit}>
          <header>
            <span>BRAMBLE</span>
            <h1>Open the story room.</h1>
            <p>
              Your stories, connections, and writing team. Sign in to pick up
              where you left off.
            </p>
          </header>

          <label className="password-field">
            <span>USERNAME</span>
            <div>
              <select
                autoFocus
                autoComplete="username"
                name="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
                disabled={busy}
              >
                <option value="silacel">silacel</option>
                <option value="spunkySarb">spunkySarb</option>
              </select>
            </div>
          </label>
          <label className="password-field">
            <span>PASSWORD</span>
            <div>
              <KeyRound size={16} />
              <input
                autoComplete="current-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Your password"
                aria-describedby={error ? 'login-error' : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPassword((current) => !current)}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>

          {error && (
            <p className="login-error" id="login-error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="login-submit"
            disabled={!username.trim() || !password || busy}
          >
            {busy ? (
              <LoaderCircle className="spin" size={16} />
            ) : (
              <KeyRound size={16} />
            )}
            {busy ? 'Opening…' : 'Enter Bramble'}
          </button>

          <footer>
            <span>
              <i /> SERVER VERIFIED
            </span>
            <span>7-DAY SESSION</span>
          </footer>
        </form>
      </section>
    </main>
  )
}
